import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { conformanceSelector, enabledSelector, labelSelector, newPathSelector, optionsSelector, valueSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import { UncomposedOptionPickerField } from './optionPickerField';
import logValueOnChange from '../commonActions/logValueOnChange';

/**
 * @class IssueSummaryPicker
 */
const IssueSummaryPicker = _ref => {
  let enabled = _ref.enabled,
    job = _ref.job,
    onChange = _ref.onChange,
    _ref$reportWithNDT = _ref.reportWithNDT,
    ndtReports = _ref$reportWithNDT.list,
    ndtIndexQuery = _ref$reportWithNDT.indexQuery,
    reportWithNDTActions = _ref.reportWithNDTActions,
    _ref$reportWithMainte = _ref.reportWithMaintenance,
    maintenanceReports = _ref$reportWithMainte.list,
    maintenanceIndexQuery = _ref$reportWithMainte.indexQuery,
    reportWithMaintenanceActions = _ref.reportWithMaintenanceActions,
    _ref$summaryType = _ref.summaryType,
    summaryType = _ref$summaryType === void 0 ? 'ndt' : _ref$summaryType,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? null : _ref$value,
    _ref$allowsParentSele = _ref.allowsParentSelection,
    allowsParentSelection = _ref$allowsParentSele === void 0 ? false : _ref$allowsParentSele,
    _ref$allowsMultipleSe = _ref.allowsMultipleSelection,
    allowsMultipleSelection = _ref$allowsMultipleSe === void 0 ? false : _ref$allowsMultipleSe,
    _ref$allowsOther = _ref.allowsOther,
    allowsOther = _ref$allowsOther === void 0 ? false : _ref$allowsOther,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$conformanceText = _ref.conformanceText,
    conformanceText = _ref$conformanceText === void 0 ? null : _ref$conformanceText,
    _ref$optionsTable = _ref.optionsTable,
    optionsTable = _ref$optionsTable === void 0 ? null : _ref$optionsTable,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? null : _ref$label,
    _ref$labelKey = _ref.labelKey,
    labelKey = _ref$labelKey === void 0 ? 'title' : _ref$labelKey,
    _ref$maxTextLength = _ref.maxTextLength,
    maxTextLength = _ref$maxTextLength === void 0 ? 2024 : _ref$maxTextLength,
    _ref$onFocus = _ref.onFocus,
    onFocus = _ref$onFocus === void 0 ? () => null : _ref$onFocus,
    _ref$testingIdentifie = _ref.testingIdentifier,
    testingIdentifier = _ref$testingIdentifie === void 0 ? '' : _ref$testingIdentifie,
    _ref$valueKey = _ref.valueKey,
    valueKey = _ref$valueKey === void 0 ? 'identifier' : _ref$valueKey,
    path = _ref.path;
  const options = useMemo(() => {
    let reports = ndtReports;
    if (summaryType === 'maintenance') {
      reports = maintenanceReports;
    }
    return reports.map(report => _objectSpread(_objectSpread({}, report), {}, {
      identifier: report.id,
      title: "".concat(report.reportTypeName, " ").concat(report.reportNumber)
    }));
  }, [ndtReports, maintenanceReports, summaryType]);

  // The OptionPickerField takes content in the format
  // { id: { ...value } } so transform
  let selected = JSON.parse(JSON.stringify(value || {}));
  if (selected.id) {
    selected = {
      [selected.id]: selected
    };
  }
  const handleChange = val => {
    if (val === undefined || val.value === null) {
      return onChange({
        value: null,
        path
      });
    }
    const _Object$values = Object.values(val.value),
      _Object$values2 = _slicedToArray(_Object$values, 1),
      selection = _Object$values2[0];
    if (!selection) {
      return onChange(_objectSpread(_objectSpread({}, val), {}, {
        path
      }));
    }
    const title = selection.title,
      id = selection.id;
    return onChange(_objectSpread(_objectSpread({}, val), {}, {
      path,
      value: {
        title,
        id
      }
    }));
  };
  useEffect(() => {
    // we need to re-run the filter query when summaryType changes
    // (or when job changes for that matter)
    if (summaryType === 'maintenance') {
      reportWithMaintenanceActions.serverFilter({
        job: [job],
        ndts: false,
        maintenance: true
      }, maintenanceIndexQuery, true);
    } else if (summaryType === 'ndt') {
      reportWithNDTActions.serverFilter({
        job: [job],
        ndts: true,
        maintenance: false
      }, ndtIndexQuery, true);
    }
  }, [maintenanceIndexQuery, ndtIndexQuery, summaryType, reportWithMaintenanceActions, reportWithNDTActions, job]);
  return /*#__PURE__*/React.createElement(UncomposedOptionPickerField, {
    path: path,
    enabled: enabled,
    options: options,
    value: selected || null,
    onChange: handleChange,
    allowsMultipleSelection: allowsMultipleSelection,
    allowsOther: allowsOther,
    allowsParentSelection: allowsParentSelection,
    className: className,
    conformanceText: conformanceText,
    optionsTable: optionsTable,
    label: label,
    labelKey: labelKey,
    maxTextLength: maxTextLength,
    onFocus: onFocus,
    testingIdentifier: testingIdentifier,
    valueKey: valueKey
  });
};
IssueSummaryPicker.propTypes = {
  enabled: PropTypes.bool.isRequired,
  job: PropTypes.shape({
    id: PropTypes.string
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  reportWithNDTActions: PropTypes.shape({
    serverFilter: PropTypes.func
  }).isRequired,
  reportWithNDT: PropTypes.shape({
    indexQuery: PropTypes.shape({
      initialized: PropTypes.bool
    }),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  reportWithMaintenanceActions: PropTypes.shape({
    serverFilter: PropTypes.func
  }).isRequired,
  reportWithMaintenance: PropTypes.shape({
    indexQuery: PropTypes.shape({
      initialized: PropTypes.bool
    }),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  summaryType: PropTypes.oneOf(['ndt', 'maintenance']),
  allowsMultipleSelection: PropTypes.bool,
  allowsOther: PropTypes.bool,
  /* eslint-disable-next-line react/require-default-props */
  allowsParentSelection: PropTypes.bool,
  // defaults are assigned at a lower level
  className: PropTypes.string,
  conformanceText: PropTypes.string,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  maxTextLength: PropTypes.number,
  onFocus: PropTypes.func,
  optionsTable: PropTypes.string,
  testingIdentifier: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({}), PropTypes.string]),
  valueKey: PropTypes.string
};
export { IssueSummaryPicker as UncomposedIssueSummaryPicker };
export default entityComposition(IssueSummaryPicker, ['reportWithNDT', 'reportWithMaintenance'], {
  enabled: enabledSelector,
  value: valueSelector,
  job: state => state.report.active.Job,
  // do not use the newPathSelector on this component - it
  // is already done on the OptionPickerField it makes use of.

  conformanceText: conformanceSelector,
  label: labelSelector,
  options: optionsSelector,
  onChange: logValueOnChange,
  path: newPathSelector
});